import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getOrgId } from '../services/orgStore';
import * as xeroStatusService from '../services/xeroStatusService';

const XeroStatusContext = createContext(null);

/**
 * Holds Xero "connected for this user + org" and refetches when
 * `navigationEpoch` changes (e.g. sidebar view) so status stays fresh after OAuth or disconnect.
 */
export function XeroStatusProvider({ navigationEpoch, children }) {
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [tenantId, setTenantId] = useState(null);
  const [tenantName, setTenantName] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    const orgId = getOrgId();
    if (!orgId) {
      setLoading(false);
      setConnected(false);
      setTenantId(null);
      setTenantName(null);
      setUpdatedAt(null);
      setError('');
      return;
    }
    setLoading(true);
    try {
      const data = await xeroStatusService.fetchXeroStatus(orgId);
      setConnected(!!data.connected);
      setTenantId(data.tenant_id);
      setTenantName(data.tenant_name);
      setUpdatedAt(data.updated_at);
      setError(data.error || '');
    } catch (e) {
      setConnected(false);
      setTenantId(null);
      setTenantName(null);
      setUpdatedAt(null);
      setError(e?.message || 'Failed to load Xero status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh, navigationEpoch]);

  const value = useMemo(
    () => ({
      loading,
      connected,
      tenantId,
      tenantName,
      updatedAt,
      error,
      refresh,
    }),
    [loading, connected, tenantId, tenantName, updatedAt, error, refresh]
  );

  return (
    <XeroStatusContext.Provider value={value}>
      {children}
    </XeroStatusContext.Provider>
  );
}

export function useXeroStatus() {
  const ctx = useContext(XeroStatusContext);
  if (!ctx) {
    throw new Error('useXeroStatus must be used within XeroStatusProvider');
  }
  return ctx;
}
